import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, Divider, Progress } from "antd";
import { TimeUtil } from "../../utils/time";
import BacklinksSummary from './BacklinksSummary';
import TopKeywordsSummary from './TopKeywordsSummary';
import SiteSpeedSummary from './SiteSpeedSummary';
import SiteOutageSummary from './SiteOutageSummary';
import SkeletonBox from '../SkeletonBox';
import CardTitle from '../CardTitle';
import { useFetchBacklinksOverviewQuery } from '../../backlinks/service';
import { getBacklinksOverviewForInterval } from '../../backlinks/utils';
import { useFetchTopKeywordsOverviewQuery } from '../../topKeywords/service';
import { useFetchLoadingTimeQuery } from '../../siteSpeed/service';
import { useFetchOutageListQuery } from '../../outage/service';
import { WebsiteIndexEntry } from '../../deepAnalysis/websiteIndexEntry/types';
import { getProgressColorByScore } from '../../deepAnalysis/utils';
import { useFetchOnPageSeoScoreQuery } from "../../deepAnalysis/websiteIndexEntry/service";
import './Summary.less';


type Props = {
    selectedWebsite: WebsiteIndexEntry;
    homepageScreenshotUrl: string | null;
    isRunningDeepAnalysis: boolean;
    isLoadingDeepAnalysis: boolean;
};

type ImageWithLoaderProps = {
    src: string;
    alt: string;
    width: number | string;
    height: number | string;
};

function ImageWithLoader({ src, alt, width, height }: ImageWithLoaderProps) {
    const [isLoaded, setIsLoaded] = useState(false);

    return (
        <div style={{ position: "relative", width, height }}>
            {/* {!isLoaded && <Icon className="loader" type="loading" />} */}

            <img
                src={src}
                alt={alt}
                onLoad={() => setIsLoaded(true)}
                onError={() => console.error("Failed to load image")}
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    opacity: isLoaded ? 1 : 0,
                    transition: "opacity 0.3s ease",
                }}
            />
        </div>
    );
}

export default function Summary({ selectedWebsite, homepageScreenshotUrl, isRunningDeepAnalysis, isLoadingDeepAnalysis }: Props) {
    const isFirstTopKeywordsFetchRef = useRef<boolean>(true);

    const currentDate = TimeUtil.utcNow();
    const sevenDaysAgo = TimeUtil.subtractDays(currentDate, 7);

    const url = selectedWebsite.homepage_url;
    const domain = selectedWebsite.domain;
    const locationName = selectedWebsite.settings.location_name;
    const languageCode = selectedWebsite.settings.language_code;
    const mainMonitoringRegion = selectedWebsite.settings.regions[0];

    const { data: onPageSeoScore, isLoading: isLoadingOnPageSeoScore } = useFetchOnPageSeoScoreQuery({ guid: selectedWebsite.guid });
    const { data: backlinksOverview, isLoading: isLoadingBacklinks } = useFetchBacklinksOverviewQuery({ domain });
    const { data: topKeywordsOverview, isLoading: isLoadingTopKeywords } = useFetchTopKeywordsOverviewQuery({ domain, locationName, languageCode });

    const { data: siteSpeed, isLoading: isLoadingSiteSpeed } = useFetchLoadingTimeQuery({
        url,
        region: mainMonitoringRegion,
        startDate: sevenDaysAgo.format('YYYY-MM-DDT00:00:00'),
        endDate: currentDate.format('YYYY-MM-DDT00:00:00'),
        interval: 'last_7_days',
        groupBy: 'day',
    });

    const { data: outageList, isLoading: isLoadingOutages } = useFetchOutageListQuery({
        url,
        startDate: sevenDaysAgo.format('YYYY-MM-DDTHH:mm:ss'),
        endDate: currentDate.format('YYYY-MM-DDTHH:mm:ss'),
    });

    const intervalBacklinksOverview = useMemo(
        () =>
            backlinksOverview ? getBacklinksOverviewForInterval(backlinksOverview, sevenDaysAgo.format('YYYY-MM-DD'), currentDate.format('YYYY-MM-DD')) : undefined,
        [backlinksOverview, currentDate, sevenDaysAgo]
    );

    useEffect(() => {
        if (!isLoadingTopKeywords) {
            isFirstTopKeywordsFetchRef.current = false;
        }
    }, [isLoadingTopKeywords]);

    const isLoading =
        isLoadingDeepAnalysis ||
        isLoadingOnPageSeoScore ||
        isLoadingBacklinks ||
        (isLoadingTopKeywords && isFirstTopKeywordsFetchRef.current) ||
        isLoadingSiteSpeed ||
        isLoadingOutages;

    return (
        <div className="dashboard-seo-analysis-summary">
            {isLoading && <SkeletonBox />}

            {!isLoading && (
                <Card
                    title={
                        <CardTitle title={domain} description="Summary" />
                    }
                >
                    {isRunningDeepAnalysis ? null : (
                        <div className="header">
                            <div className="monitor">
                                {homepageScreenshotUrl ?
                                    // <img width="262" height="148" alt="homepage-screenshot" src={homepageScreenshotUrl} /> 
                                    <ImageWithLoader src={homepageScreenshotUrl} alt="homepage-screenshot" width="262px" height="148px" />
                                    : <div className="no-screenshot" style={{ width: "262px", height: "148px" }}></div>}
                            </div>
                            <div className="seo-score">
                                <Progress
                                    type="circle"
                                    percent={onPageSeoScore?.overall_score || 0}
                                    strokeWidth={6}
                                    strokeColor={getProgressColorByScore(onPageSeoScore?.overall_score || 0)}
                                    format={(percent) => `${percent}/100`}
                                />
                                <span className="progress-label">Domain Score</span>
                            </div>
                        </div>
                    )}


                    <BacklinksSummary
                        totalBacklinks={intervalBacklinksOverview?.total_backlinks.map((backlinks) => backlinks.value) || []}
                        websiteGuid={selectedWebsite.guid}
                    />
                    <Divider />

                    <TopKeywordsSummary
                        averagePosition={topKeywordsOverview?.avg_pos}
                        websiteGuid={selectedWebsite.guid}
                    />
                    <Divider />

                    <SiteSpeedSummary
                        loadingTimes={siteSpeed?.reports.map((data) => data.loading_time) || []}
                        websiteGuid={selectedWebsite.guid}
                    />
                    <Divider />
                    <SiteOutageSummary
                        hasOutages={Object.keys(outageList?.outages.active || {}).length > 0}
                        websiteGuid={selectedWebsite.guid}
                    />
                </Card>
            )}
        </div>
    );
}
